import { useTableSortingAndPagination } from "#redux/reducers/tableStateReducer";
import { linkToFormSubmissionDetail } from "#routers/links";
import {
  FormSubmissionsTableFilterArea,
  FormSubmissionsTableFilterAreaDrawerContent,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { useAddFormSubmissionContext } from "#src/batteries-included-components/Layouts/Form/Submission/Add/AddFormSubmission/AddFormSubmissionContext";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { getStatusType } from "#src/components/Common/Table/rendererHelper";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  ItemAction,
  Pill,
} from "@validereinc/common-components";
import {
  FormSubmissionAdapter,
  FormSubmissionGetListRequestType,
  FormSubmissionStatus,
  FormSubmissionType,
  Resources,
  SortDirection,
} from "@validereinc/domain";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import { isEmpty } from "es-toolkit/compat";
import React, { useEffect, useMemo } from "react";
import {
  getFilteredSubmissionIdsByAnswerSearch,
  getFormattedFiltersBasedOnFormSchema,
  getFormSubmissionQueryPayloadWithAnswerFilter,
  getHeadersFromSchema,
} from "../FormSubmissionsTablePanel.helpers";

export const AddFormSubmissionsTablePanel = ({
  shouldHideDrafts = true,
}: {
  // submissions of status "draft" are hidden by default
  shouldHideDrafts?: boolean;
}) => {
  const {
    selectedFormSubmissions,
    selectFormSubmission,
    deselectFormSubmission,
    filterConfigStorageKey,
    tableConfigStorageKey,
    tableStateStorageKey,
    viewConfigStorageKey,
    filtersDefaultValues,
  } = useAddFormSubmissionContext();
  const [viewFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    viewConfigStorageKey
  );
  const [tableFilters] = useStickyState<FormSubmissionGetListRequestType>(
    {},
    filterConfigStorageKey
  );
  const filters = useMemo(
    () => ({
      ...tableFilters,
      ...viewFilters,
    }),
    [tableFilters, viewFilters]
  );
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );
  // @ts-expect-error TODO: fix filter typing
  const { created_at, form_schema_id, created_by, ...restFilters } = filters;

  const schemaQuery = useGetOneFormSchema(
    { id: form_schema_id ?? "" },
    { enabled: Boolean(form_schema_id) }
  );
  const formSchema = schemaQuery.data?.data;

  const formattedAnswerFilters = useMemo(
    () =>
      formSchema
        ? getFormattedFiltersBasedOnFormSchema(restFilters, formSchema)
        : null,
    [restFilters, formSchema, created_at, created_by]
  );
  const hasFormattedAnswerFilters = !isEmpty(formattedAnswerFilters);

  const answersQueryFilter: any = {
    created_at,
    ...(created_by ? { created_by: created_by } : {}),
    ...(shouldHideDrafts &&
    // @ts-expect-error TODO: fix filter typing
    (!restFilters.status ||
      // @ts-expect-error TODO: fix filter typing
      (Array.isArray(restFilters.status) && !restFilters.status?.length))
      ? {
          status: Object.values(FormSubmissionStatus).filter(
            (s) => s !== FormSubmissionStatus.DRAFT
          ),
        }
      : // @ts-expect-error TODO: fix filter typing
        { status: restFilters.status }),
    ...formattedAnswerFilters,
  };

  const {
    data: answersQuery,
    hasNextPage: answersQueryHasNextPage,
    isFetching: answersQueryIsFetching,
    isLoading: answersQueryIsLoading,
    fetchNextPage: answerQueryFetchNextPage,
    refetch: refetchAnswers,
  } = useInfiniteQuery({
    queryKey: ["forms", "submissions", "answers", answersQueryFilter],
    queryFn: ({ pageParam }) => {
      if (!form_schema_id || !hasFormattedAnswerFilters) return;

      return FormSubmissionAdapter.answers.getListV2({
        page: pageParam ?? 1,
        pageSize: 500,
        sortBy: tableState.sortBy,
        sortDirection: tableState.sortDirection,
        filters: answersQueryFilter,
        meta: {
          form_schema_id: form_schema_id as string,
        },
      });
    },
    getNextPageParam: (resp) => {
      return resp
        ? resp.page_number < resp.total_pages
          ? resp.page_number + 1
          : undefined
        : undefined;
    },
    enabled: Boolean(form_schema_id) && hasFormattedAnswerFilters,
    staleTime: 2 * 60 * 1000,
  });

  const filteredSubmissionIdsByAnswerSearch = useMemo(
    () => getFilteredSubmissionIdsByAnswerSearch(answersQuery),
    [answersQuery?.pages]
  );

  // name is constructed by the FE and can't be used by the BE to search
  // @ts-expect-error TODO: fix filter typing
  delete restFilters.name;
  const queryPayloadWithoutAnswerFilter: Parameters<
    typeof FormSubmissionAdapter.getList
  >[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    filters: {
      created_at,
      ...toFlattenedObject({
        ...(form_schema_id ? { "form_schema.id": form_schema_id } : {}),
        "form_schema.status": "active",
      }),
      ...(shouldHideDrafts &&
      // @ts-expect-error TODO: fix filter typing
      (!restFilters.status ||
        // @ts-expect-error TODO: fix filter typing
        (Array.isArray(restFilters.status) && !restFilters.status?.length))
        ? {
            status: Object.values(FormSubmissionStatus).filter(
              (s) => s !== FormSubmissionStatus.DRAFT
            ),
          }
        : {}),
    },
    meta: { answers: true },
  };

  const queryPayload = useMemo(
    () =>
      getFormSubmissionQueryPayloadWithAnswerFilter(
        queryPayloadWithoutAnswerFilter,
        hasFormattedAnswerFilters,
        filteredSubmissionIdsByAnswerSearch
      ),
    [
      queryPayloadWithoutAnswerFilter,
      hasFormattedAnswerFilters,
      filteredSubmissionIdsByAnswerSearch,
    ]
  );

  const { data: formSubmissionData, isLoading } = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, queryPayload],
    queryFn: () => {
      return FormSubmissionAdapter.getList(queryPayload);
    },
    enabled:
      !hasFormattedAnswerFilters ||
      (!answersQueryIsLoading && !answersQueryIsFetching),
    staleTime: 2 * 60 * 1000,
  });

  const { measurementUnits, getPrecisionByType } = useMeasurementTypes();

  const dynamicSubmissionsHeaders = useMemo(
    () =>
      formSchema
        ? getHeadersFromSchema(formSchema, {
            measurementUnits,
            getPrecisionByType,
          })
        : [],
    [formSchema, measurementUnits, getPrecisionByType]
  );

  const submissionsHeaders: Array<HeaderType<FormSubmissionType>> = [
    {
      label: "Submission Name",
      key: "id",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink to={linkToFormSubmissionDetail(item.id)}>
          {`${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`}
        </RoutingLink>
      ),
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }: { item: FormSubmissionType }) => (
        <Pill variant={getStatusType(item.status, "form").type}>
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Template",
      key: "form_schema_id",
      isSortable: true,
      renderComponent: ({ item }) => (
        <RoutingLink
          to={linkToFormTemplateDetail(
            item.form_schema?.form_category_id,
            item.form_schema_id
          )}
        >
          {item.form_schema?.name}
        </RoutingLink>
      ),
    },
    {
      label: "Submission Date",
      key: "created_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.created_at}
          withTime
        />
      ),
    },
    ...dynamicSubmissionsHeaders,
  ];

  const items = useMemo(
    (): Array<FormSubmissionType & { name: string }> =>
      formSubmissionData?.data?.reduce(
        (
          itemsWithNames: Array<FormSubmissionType & { name: string }>,
          item: FormSubmissionType
        ) => {
          const name = `${item?.form_schema?.name} - ${item?.id?.slice(0, 7)}`;
          const newItem = { ...item, name };
          // @ts-expect-error TODO: fix filter typing
          if (typeof filters.name === "string") {
            if (
              // @ts-expect-error TODO: fix filter typing
              newItem.name.toLowerCase().includes(filters.name.toLowerCase())
            ) {
              itemsWithNames.push(newItem);
            }
          } else {
            itemsWithNames.push(newItem);
          }

          return itemsWithNames;
        },
        []
      ) ?? [],
    // @ts-expect-error TODO: fix filter typing
    [formSubmissionData, filters.name]
  );

  useEffect(() => {
    if (answersQueryHasNextPage && !answersQueryIsFetching) {
      answerQueryFetchNextPage();
    }
  }, [answersQueryHasNextPage, answersQueryIsFetching]);

  useEffect(() => {
    refetchAnswers();
  }, [refetchAnswers, form_schema_id]);

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Form Submissions",
      }}
      filterComponent={
        <FormSubmissionsTableFilterArea
          defaultValues={filtersDefaultValues}
          filterConfigStorageKey={filterConfigStorageKey}
          shouldShowFormSubmissionSearchInput
          shouldShowSavedFilters
          filterDrawerContentSlot={
            <FormSubmissionsTableFilterAreaDrawerContent
              hasStatusFilter
              hasSubmittedByFilter
              formSchemaId={(form_schema_id as string) ?? ""}
            />
          }
        />
      }
      dataTableProps={{
        items,
        getItemId: (item) => item.id,
        headers: submissionsHeaders,
        isLoading:
          isLoading || (hasFormattedAnswerFilters && answersQueryIsLoading),
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: formSubmissionData?.total_entries,
        },
        onPaginationChange: updateTableState,
        onSortChange: updateTableState,
        getItemActions: ({
          item,
        }: {
          item: FormSubmissionType;
        }): ItemAction[] =>
          selectedFormSubmissions?.find((selected) => selected.id === item.id)
            ? [
                {
                  label: "Remove",
                  buttonProps: {
                    onClick: () => deselectFormSubmission(item?.id),
                    icon: "minus-circle",
                  },
                },
              ]
            : [
                {
                  label: "Add",
                  buttonProps: {
                    onClick: () => selectFormSubmission(item),
                    icon: "plus-circle",
                  },
                },
              ],
      }}
    />
  );
};
